import React, { useState } from 'react';
import { Card, Button, Collapse, Badge, Alert } from 'react-bootstrap';
import { DollarSign, ChevronDown, ChevronUp, TrendingUp, Clock, AlertTriangle, Edit2 } from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import UpdateCompensationModal from './UpdateCompensationModal';

const CompensationCard = ({ employeeId, companyId, currentCompensation, history, currentContract, onUpdate }) => {
  const [showHistory, setShowHistory] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'

  const handleCreateClick = () => {
    if (!currentContract) {
      toast.error('Ett anställningskontrakt måste finnas innan ersättning kan läggas till');
      return;
    }
    setModalMode('create');
    setShowUpdateModal(true);
  };

  const handleEditClick = () => {
    setModalMode('edit');
    setShowUpdateModal(true);
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return `${parseFloat(amount).toLocaleString('sv-SE')} SEK`;
  };

  const formatDate = (date) => {
    return moment(date).format('DD MMM YYYY');
  };

  const handleUpdateSuccess = () => {
    setShowUpdateModal(false);
    if (onUpdate) onUpdate();
  };

  if (!currentCompensation) {
    return (
      <div className="info-section mb-4" role="region" aria-labelledby="compensation-heading">
        <div className="p-4">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <DollarSign size={20} className="me-3 text-warning" aria-hidden="true" />
              <div>
                <h5 className="mb-0 fw-bold" id="compensation-heading">Aktuell ersättning</h5>
                <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                  Ingen ersättning registrerad
                </p>
              </div>
            </div>
          </div>
          <div className="section-divider"></div>
          <div className="text-center py-5">
            <div className="mb-4">
              <div className="d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: '80px', height: '80px' }}>
                <DollarSign size={40} className="text-warning" aria-hidden="true" />
              </div>
            </div>
            <h5 className="mb-2">Ingen ersättning har registrerats än</h5>
            <p className="text-muted mb-4">
              Lägg till löneuppgifter för att kunna hantera lön och lönespecifikationer för denna anställd.
            </p>
            <Button
              variant="primary"
              size="lg"
              className="action-button d-flex align-items-center mx-auto"
              onClick={handleCreateClick}
              aria-label="Lägg till ersättning för anställd"
            >
              <TrendingUp size={18} className="me-2" aria-hidden="true" />
              Lägg till lön nu
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="info-section mb-4" role="region" aria-labelledby="compensation-heading">
        <div className="p-4">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <DollarSign size={20} className="me-3 text-warning" aria-hidden="true" />
              <div>
                <h5 className="mb-0 fw-bold" id="compensation-heading">
                  {currentCompensation._isFuture ? 'Kommande ersättning' : 'Aktuell ersättning'}
                </h5>
                <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                  {currentCompensation._isFuture ? 'Börjar' : 'Gäller från'} {formatDate(currentCompensation.valid_from)}
                </p>
              </div>
              {currentCompensation._isFuture && (
                <span className="badge bg-primary ms-2" style={{ fontSize: '0.75rem' }}>
                  Kommande
                </span>
              )}
            </div>
            <Button
              variant="primary"
              className="action-button d-flex align-items-center"
              onClick={handleCreateClick}
              aria-label="Skapa ny ersättning för anställd"
            >
              <TrendingUp size={16} className="me-2" aria-hidden="true" />
              Skapa ny ersättning
            </Button>
          </div>
          <div className="section-divider"></div>
          {/* Current Compensation Display */}
          <div className="row g-3 mb-3">
            <div className="col-md-6 col-lg-3">
              <div className="p-3 border rounded-lg position-relative">
                <Button
                  variant="link"
                  size="sm"
                  className="position-absolute top-0 end-0 mt-2 me-2 p-1 text-primary"
                  onClick={handleEditClick}
                  aria-label="Redigera ersättning"
                  style={{ minWidth: 'auto' }}
                >
                  <Edit2 size={16} />
                </Button>
                <div className="text-muted small mb-1">Månadslön</div>
                <div className="h5 mb-0 fw-bold text-success">
                  {formatCurrency(currentCompensation.amount_monthly)}
                </div>
              </div>
            </div>
            <div className="col-md-6 col-lg-3">
              <div className="p-3 border rounded-lg position-relative">
                <Button
                  variant="link"
                  size="sm"
                  className="position-absolute top-0 end-0 mt-2 me-2 p-1 text-primary"
                  onClick={handleEditClick}
                  aria-label="Redigera ersättning"
                  style={{ minWidth: 'auto' }}
                >
                  <Edit2 size={16} />
                </Button>
                <div className="text-muted small mb-1">Timlön</div>
                <div className="h6 mb-0">
                  {formatCurrency(currentCompensation.amount_hourly)}
                </div>
              </div>
            </div>
            <div className="col-md-6 col-lg-3">
              <div className="p-3 border rounded-lg position-relative">
                <Button
                  variant="link"
                  size="sm"
                  className="position-absolute top-0 end-0 mt-2 me-2 p-1 text-primary"
                  onClick={handleEditClick}
                  aria-label="Redigera ersättning"
                  style={{ minWidth: 'auto' }}
                >
                  <Edit2 size={16} />
                </Button>
                <div className="text-muted small mb-1">Daglön</div>
                <div className="h6 mb-0">
                  {formatCurrency(currentCompensation.amount_daily)}
                </div>
              </div>
            </div>
            <div className="col-md-6 col-lg-3">
              <div className="p-3 border rounded-lg position-relative">
                <Button
                  variant="link"
                  size="sm"
                  className="position-absolute top-0 end-0 mt-2 me-2 p-1 text-primary"
                  onClick={handleEditClick}
                  aria-label="Redigera ersättning"
                  style={{ minWidth: 'auto' }}
                >
                  <Edit2 size={16} />
                </Button>
                <div className="text-muted small mb-1">Veckolön</div>
                <div className="h6 mb-0">
                  {formatCurrency(currentCompensation.amount_weekly)}
                </div>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="row g-2">
            <div className="col-auto">
              <Badge bg="secondary" className="py-2 px-3">
                {currentCompensation.pay_frequency === 'monthly' ? 'Månadslön' :
                 currentCompensation.pay_frequency === 'hourly' ? 'Timlön' :
                 currentCompensation.pay_frequency === 'weekly' ? 'Veckolön' : 'Daglön'}
              </Badge>
            </div>
            <div className="col-auto">
              <Badge bg="info" className="py-2 px-3">
                {currentCompensation.currency || 'SEK'}
              </Badge>
            </div>
            {currentCompensation.work_week_hours && (
              <div className="col-auto">
                <Badge bg="light" text="dark" className="py-2 px-3">
                  {currentCompensation.work_week_hours}h/vecka
                </Badge>
              </div>
            )}
          </div>

          {/* History Toggle */}
          {history && history.length > 1 && (
            <div className="mt-3">
              <Button
                variant="link"
                size="sm"
                onClick={() => setShowHistory(!showHistory)}
                className="d-flex align-items-center p-0 text-decoration-none"
              >
                {showHistory ? <ChevronUp size={16} className="me-1" /> : <ChevronDown size={16} className="me-1" />}
                {showHistory ? 'Dölj' : 'Visa'} lönehistorik ({history.length - 1} tidigare ändringar)
              </Button>

              <Collapse in={showHistory}>
                <div className="mt-3">
                  <table className="table table-sm table-hover">
                    <thead>
                      <tr>
                        <th>Period</th>
                        <th>Månadslön</th>
                        <th>Frekvens</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((comp, index) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        
                        const validFrom = new Date(comp.valid_from);
                        validFrom.setHours(0, 0, 0, 0);
                        
                        const validTo = comp.valid_to ? new Date(comp.valid_to) : null;
                        if (validTo) {
                          validTo.setHours(0, 0, 0, 0);
                        }
                        
                        // Determine status based on date ranges
                        let status = 'previous';
                        let statusLabel = 'Tidigare';
                        let statusBg = 'secondary';
                        
                        if (validFrom > today) {
                          // Future compensation (starts after today)
                          status = 'upcoming';
                          statusLabel = 'Kommande';
                          statusBg = 'info';
                        } else if (validFrom <= today && (!validTo || validTo >= today)) {
                          // Current compensation (started on/before today and hasn't ended yet)
                          status = 'current';
                          statusLabel = 'Aktuell';
                          statusBg = 'success';
                        }
                        
                        const isCurrent = status === 'current';
                        
                        return (
                          <tr key={comp.comp_id} className={isCurrent ? 'table-active' : ''}>
                            <td>
                              {formatDate(comp.valid_from)}
                              {comp.valid_to ? ` - ${formatDate(comp.valid_to)}` : ' - Pågående'}
                            </td>
                            <td className="fw-semibold">
                              {formatCurrency(comp.amount_monthly)}
                            </td>
                            <td>
                              <span className="text-capitalize">{comp.pay_frequency}</span>
                            </td>
                            <td>
                              <Badge bg={statusBg}>{statusLabel}</Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Collapse>
            </div>
          )}
        </div>
      </div>

      <UpdateCompensationModal
        show={showUpdateModal}
        handleClose={() => setShowUpdateModal(false)}
        employeeId={employeeId}
        companyId={companyId}
        currentCompensation={modalMode === 'edit' ? currentCompensation : null}
        currentContract={currentContract}
        mode={modalMode}
        onSuccess={handleUpdateSuccess}
      />
    </>
  );
};

export default CompensationCard;
